import React, { useState, useEffect, useCallback } from 'react';

// Documents attached to a deal — backed by /api/documents (RLS-scoped; bytes in Tigris). Upload a
// file with a doc_type tag, see the list, download. Matches the existing card palette/typography.

// doc_type options offered in the upload dropdown.
const DOC_TYPES = [
  { value: 'fully_executed_purchase_contract',   label: 'Fully Executed Purchase Contract' },
  { value: 'fully_executed_assignment_agreement', label: 'Fully Executed Assignment Agreement' },
  { value: 'seller_disclosure',                  label: 'Seller Disclosure' },
  { value: 'earnest_money_receipt',              label: 'Earnest Money Receipt' },
  { value: 'proof_of_funds',                     label: 'Proof of Funds' },
  { value: 'addendum',                           label: 'Addendum' },
  { value: 'other',                              label: 'Other' },
];
// Labels for display — includes the system-generated types from contract generation.
const TYPE_LABELS = {
  ...Object.fromEntries(DOC_TYPES.map(t => [t.value, t.label])),
  generated_purchase_contract: 'Generated Purchase Contract',
  executed_purchase_contract:  'Executed Purchase Contract',
  generated_assignment:        'Generated Assignment',
};
const labelFor = (t) => TYPE_LABELS[t] || (t ? t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Document');

function fmtSize(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
}

// compact: the Deal page's card style (smaller title/padding, no bottom margin) instead of the
// Underwriting tab's. refreshKey: bump to re-fetch — e.g. after a contract is generated elsewhere on
// the page, so the new PDF shows up without a reload.
export default function DealDocuments({ dealId, onStageChange, compact = false, refreshKey = 0 }) {
  const [docs, setDocs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [forbidden, setForbidden] = useState(false);   // GET 403: not the deal owner or an org admin
  const [docType, setDocType] = useState('other');
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);   // bump to reset the <input type=file>
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    fetch(`/api/documents?deal_id=${dealId}`, { credentials: 'include' })
      .then(async r => {
        setForbidden(r.status === 403);
        const d = await r.json().catch(() => ({}));
        setDocs(r.ok ? (d.documents || []) : []);
      })
      .catch(() => setDocs([]))
      .finally(() => setLoaded(true));
  }, [dealId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const shell = compact
    ? { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: '18px 20px' }
    : { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 24, marginBottom: 20 };
  const titleStyle = compact
    ? { fontSize: 14, fontWeight: 700, color: 'var(--white)', marginBottom: 12 }
    : { fontSize: 16, fontWeight: 700, color: 'var(--white)', marginBottom: 16 };
  const title = <>Documents{loaded && !forbidden && docs.length > 0 && <span style={{ color: 'var(--gray-500)', fontWeight: 600 }}> · {docs.length}</span>}</>;

  // Documents are contracts with the purchase price in them — same gate as the deal's money fields.
  if (forbidden) {
    return (
      <div style={shell}>
        <div style={titleStyle}>Documents</div>
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Only the deal owner or an org admin can see this deal's documents.</div>
      </div>
    );
  }

  const upload = async () => {
    if (!file) return;
    setUploading(true); setErr(null);
    try {
      const qs = new URLSearchParams({ deal_id: String(dealId), doc_type: docType, filename: file.name });
      const r = await fetch(`/api/documents?${qs.toString()}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Upload failed');
      // Step 5 — tagging "Fully Executed Purchase Contract" auto-advances the deal to Under Contract
      // server-side (via the stage chokepoint). When that move actually happened, tell the parent so
      // the stage badge / page data refresh.
      if (data.stage_transition && data.stage_transition.changed && onStageChange) onStageChange(data.stage_transition);
      setFile(null); setDocType('other'); setFileKey(k => k + 1);
      load();
    } catch (e) { setErr(e.message); } finally { setUploading(false); }
  };

  return (
    <div style={shell}>
      <div style={titleStyle}>{title}</div>

      {/* Upload control */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: docs.length ? 18 : 0 }}>
        <select value={docType} onChange={e => setDocType(e.target.value)}
          style={{ padding: '8px 11px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13, outline: 'none' }}>
          {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input key={fileKey} type="file" onChange={e => setFile(e.target.files[0] || null)}
          style={{ fontSize: 12, color: 'var(--gray-300)' }} />
        <button onClick={upload} disabled={!file || uploading}
          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: (!file || uploading) ? 'var(--navy-border)' : 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: (!file || uploading) ? 'default' : 'pointer' }}>
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        {err && <span style={{ fontSize: 12, color: '#B85450' }}>{err}</span>}
      </div>

      {/* List */}
      {!loaded ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 14 }}>Loading documents…</div>
      ) : docs.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 14 }}>No documents yet.</div>
      ) : (
        <div>
          {docs.map(d => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--navy-border)' }}>
              <div style={{ minWidth: 0 }}>
                <a href={`/api/documents/${d.id}/download`}
                  style={{ fontSize: 13, color: 'var(--teal)', fontWeight: 600, textDecoration: 'none', wordBreak: 'break-all' }}>
                  {d.filename}
                </a>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                  {labelFor(d.doc_type)}{d.byte_size ? ` · ${fmtSize(d.byte_size)}` : ''}{d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString()}` : ''}
                </div>
              </div>
              <a href={`/api/documents/${d.id}/download`}
                style={{ flexShrink: 0, fontSize: 12, color: 'var(--gray-300)', textDecoration: 'none', padding: '6px 12px', border: '1px solid var(--navy-border)', borderRadius: 8 }}>
                Download
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
